/**
 * Unit tables for the chemistry quantity calculators.
 *
 * Each family converts through one base unit (grams, litres, moles, mol/L). Factors are
 * exact definitions or the SI/NIST values they are quoted from:
 *
 * - avoirdupois pound = 0.453 592 37 kg and ounce = 28.349 523 125 g (exact, by the 1959
 *   international yard and pound agreement);
 * - US gallon = 3.785 411 784 L, US fluid ounce = 0.029 573 529 562 5 L (exact, NIST
 *   Handbook 44 / SP 811);
 * - inch = 2.54 cm exactly, so in³ = 16.387 064 mL and ft³ = 28 316.846 592 mL.
 *
 * Unit symbols are not translated, so they live here as data rather than in a widget's
 * strings.
 */

export interface UnitDef {
  id: string;
  /** What the reader sees and what the unit is called in a formula. */
  symbol: string;
  /** Size of one unit in the family's base unit. */
  factor: number;
}

export interface UnitFamily {
  base: string;
  units: readonly UnitDef[];
}

export const MASS: UnitFamily = {
  base: "g",
  units: [
    { id: "ug", symbol: "µg", factor: 1e-6 },
    { id: "mg", symbol: "mg", factor: 1e-3 },
    { id: "g", symbol: "g", factor: 1 },
    { id: "kg", symbol: "kg", factor: 1e3 },
    { id: "oz", symbol: "oz", factor: 28.349_523_125 },
    { id: "lb", symbol: "lb", factor: 453.592_37 },
  ],
};

export const VOLUME: UnitFamily = {
  base: "L",
  units: [
    { id: "uL", symbol: "µL", factor: 1e-6 },
    { id: "mL", symbol: "mL", factor: 1e-3 },
    { id: "cm3", symbol: "cm³", factor: 1e-3 },
    { id: "L", symbol: "L", factor: 1 },
    { id: "m3", symbol: "m³", factor: 1e3 },
    { id: "in3", symbol: "in³", factor: 0.016_387_064 },
    { id: "ft3", symbol: "ft³", factor: 28.316_846_592 },
    { id: "gal", symbol: "gal (US)", factor: 3.785_411_784 },
    { id: "floz", symbol: "fl oz (US)", factor: 0.029_573_529_562_5 },
  ],
};

export const AMOUNT: UnitFamily = {
  base: "mol",
  units: [
    { id: "umol", symbol: "µmol", factor: 1e-6 },
    { id: "mmol", symbol: "mmol", factor: 1e-3 },
    { id: "mol", symbol: "mol", factor: 1 },
    { id: "kmol", symbol: "kmol", factor: 1e3 },
  ],
};

export const MOLARITY: UnitFamily = {
  base: "mol/L",
  units: [
    { id: "pM", symbol: "pM", factor: 1e-12 },
    { id: "nM", symbol: "nM", factor: 1e-9 },
    { id: "uM", symbol: "µM", factor: 1e-6 },
    { id: "mM", symbol: "mM", factor: 1e-3 },
    { id: "M", symbol: "M", factor: 1 },
  ],
};

export function findUnit(family: UnitFamily, id: string): UnitDef {
  return family.units.find((unit) => unit.id === id) ?? family.units[0];
}

/** Value in `from` units → value in `to` units. */
export function convert(
  value: number,
  family: UnitFamily,
  from: string,
  to: string
): number {
  return (value * findUnit(family, from).factor) / findUnit(family, to).factor;
}

export function toBase(value: number, family: UnitFamily, id: string): number {
  return value * findUnit(family, id).factor;
}

export function fromBase(
  value: number,
  family: UnitFamily,
  id: string
): number {
  return value / findUnit(family, id).factor;
}

/** Select options (value = id, label = symbol) for a family. */
export function unitOptions(
  family: UnitFamily
): { value: string; label: string }[] {
  return family.units.map((unit) => ({ value: unit.id, label: unit.symbol }));
}

/* --------------------------------------------------- concentration units */

export type ConcentrationKind = "molar" | "mass" | "percent" | "fold";

export interface ConcentrationUnit extends UnitDef {
  kind: ConcentrationKind;
}

/**
 * Concentration units a dilution can use. Units convert only within a kind, because
 * mol/L to g/L needs a molar mass and a percent needs to know what it is a percent of.
 *
 * Mass concentrations convert through g/L: 1 mg/mL = 1 g/L, 1 µg/mL = 1 mg/L, and
 * 1 ng/mL = 1 µg/L. A percent is left as a pure number of "parts per hundred" and a fold
 * concentration (10×) as a pure multiple, so both dilute by the same C₁V₁ = C₂V₂.
 */
export const CONCENTRATION_UNITS: readonly ConcentrationUnit[] = [
  { id: "M", symbol: "M", factor: 1, kind: "molar" },
  { id: "mM", symbol: "mM", factor: 1e-3, kind: "molar" },
  { id: "uM", symbol: "µM", factor: 1e-6, kind: "molar" },
  { id: "nM", symbol: "nM", factor: 1e-9, kind: "molar" },
  { id: "gL", symbol: "g/L", factor: 1, kind: "mass" },
  { id: "mgmL", symbol: "mg/mL", factor: 1, kind: "mass" },
  { id: "mgL", symbol: "mg/L", factor: 1e-3, kind: "mass" },
  { id: "ugmL", symbol: "µg/mL", factor: 1e-3, kind: "mass" },
  { id: "ugL", symbol: "µg/L", factor: 1e-6, kind: "mass" },
  { id: "ngmL", symbol: "ng/mL", factor: 1e-6, kind: "mass" },
  { id: "pct", symbol: "%", factor: 1, kind: "percent" },
  { id: "x", symbol: "×", factor: 1, kind: "fold" },
];

export function findConcentrationUnit(id: string): ConcentrationUnit {
  return (
    CONCENTRATION_UNITS.find((unit) => unit.id === id) ?? CONCENTRATION_UNITS[0]
  );
}

/** Convert a concentration between units of the same kind; null across kinds. */
export function convertConcentration(
  value: number,
  from: string,
  to: string
): number | null {
  const a = findConcentrationUnit(from);
  const b = findConcentrationUnit(to);
  return a.kind === b.kind ? (value * a.factor) / b.factor : null;
}
